import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { 
  Mail, Phone, Building, MessageSquare, CheckCircle, ArrowLeft, 
  Send, Zap, Shield, Clock, MessageCircle
} from 'lucide-react';
import { toast } from 'sonner';

const API = '/api';

const TRIAL_FEATURES = [
  '7 días de prueba gratis',
  '3 perfiles de envío',
  '2,000 mensajes por día',
  'Campañas programadas',
  'Detección de respuestas',
];

export default function RequestAccess() {
  const { login } = useAuth();
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.name || !form.email || !form.phone) {
      toast.error('Completa los campos obligatorios');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/auth/request-access`, form);
      setSubmitted(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al enviar solicitud');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#09090B] flex items-center justify-center p-4">
        <div className="w-full max-w-md text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-emerald-600/10 rounded-full mb-6">
            <CheckCircle className="w-10 h-10 text-emerald-500" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">¡Registro Completado!</h1>
          <p className="text-zinc-400 mb-6">
            Tu cuenta está lista. Ahora inicia sesión con Google para activar tu prueba gratuita de 7 días.
          </p>
          
          <button
            onClick={login}
            className="w-full flex items-center justify-center gap-3 bg-white hover:bg-zinc-100 text-zinc-900 font-medium py-3 px-4 rounded-lg transition-colors mb-4"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Activar con Google
          </button>
          
          <Link
            to="/landing"
            className="inline-flex items-center gap-2 text-zinc-500 hover:text-zinc-400 text-sm"
          >
            <ArrowLeft className="w-4 h-4" />
            Volver al inicio
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#09090B] flex items-center justify-center p-4">
      <div className="w-full max-w-4xl grid md:grid-cols-2 gap-8 items-center">
        {/* Left side - Value proposition */}
        <div className="hidden md:block">
          <div className="flex items-center gap-2 mb-6">
            <div className="w-10 h-10 rounded-xl bg-blue-600 flex items-center justify-center">
              <MessageCircle className="w-6 h-6 text-white" />
            </div>
            <span className="font-bold text-xl text-white">RCS Sender</span>
          </div>
          
          <h1 className="text-3xl font-bold text-white mb-4">
            Comienza tu prueba gratuita
          </h1>
          <p className="text-zinc-400 mb-8">
            Accede a todas las funciones premium durante 7 días sin compromiso.
          </p>
          
          <div className="space-y-4">
            {TRIAL_FEATURES.map((feature, i) => (
              <div key={i} className="flex items-center gap-3">
                <div className="w-6 h-6 rounded-full bg-emerald-500/10 flex items-center justify-center flex-shrink-0">
                  <CheckCircle className="w-4 h-4 text-emerald-500" />
                </div>
                <span className="text-zinc-300">{feature}</span>
              </div>
            ))}
          </div>
          
          <div className="mt-8 p-4 bg-zinc-900/50 border border-zinc-800 rounded-xl">
            <div className="flex items-start gap-3">
              <Shield className="w-5 h-5 text-blue-400 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-white">Sin tarjeta de crédito</p>
                <p className="text-xs text-zinc-500 mt-1">
                  No necesitas ingresar datos de pago para empezar
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Right side - Form */}
        <div className="w-full max-w-md mx-auto md:mx-0">
          {/* Mobile header */}
          <div className="md:hidden text-center mb-6">
            <div className="inline-flex items-center gap-2 mb-4">
              <div className="w-8 h-8 rounded-lg bg-blue-600 flex items-center justify-center">
                <MessageCircle className="w-5 h-5 text-white" />
              </div>
              <span className="font-bold text-lg text-white">RCS Sender</span>
            </div>
            <div className="inline-flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/20 rounded-full px-3 py-1">
              <Zap className="w-4 h-4 text-emerald-400" />
              <span className="text-sm text-emerald-400 font-medium">7 días gratis</span>
            </div>
          </div>

          {/* Form Card */}
          <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-6">
            <div className="hidden md:flex items-center justify-center gap-2 bg-emerald-500/10 border border-emerald-500/20 rounded-full px-4 py-2 mb-6">
              <Zap className="w-4 h-4 text-emerald-400" />
              <span className="text-sm text-emerald-400 font-medium">Prueba gratuita de 7 días</span>
            </div>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Name */}
              <div>
                <label className="text-xs font-medium text-zinc-400 block mb-1.5">
                  Nombre Completo *
                </label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full bg-zinc-950 border border-zinc-800 focus:border-blue-500 rounded-lg px-4 py-2.5 text-white placeholder:text-zinc-600 outline-none transition-colors"
                  placeholder="Tu nombre"
                  required
                />
              </div>

              {/* Email */}
              <div>
                <label className="text-xs font-medium text-zinc-400 block mb-1.5">
                  Email *
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                  <input
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="w-full bg-zinc-950 border border-zinc-800 focus:border-blue-500 rounded-lg pl-10 pr-4 py-2.5 text-white placeholder:text-zinc-600 outline-none transition-colors"
                    placeholder="tu@email.com"
                    required
                  />
                </div>
                <p className="text-[10px] text-zinc-600 mt-1">
                  Usa el mismo email con el que iniciarás sesión en Google
                </p>
              </div>

              {/* Phone */}
              <div>
                <label className="text-xs font-medium text-zinc-400 block mb-1.5">
                  Teléfono (WhatsApp) *
                </label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                  <input
                    type="tel"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    className="w-full bg-zinc-950 border border-zinc-800 focus:border-blue-500 rounded-lg pl-10 pr-4 py-2.5 text-white placeholder:text-zinc-600 outline-none transition-colors"
                    placeholder="+54 9 11 1234-5678"
                    required
                  />
                </div>
              </div>

              {/* Company */}
              <div>
                <label className="text-xs font-medium text-zinc-400 block mb-1.5">
                  Empresa (opcional)
                </label>
                <div className="relative">
                  <Building className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                  <input
                    type="text"
                    value={form.company}
                    onChange={(e) => setForm({ ...form, company: e.target.value })}
                    className="w-full bg-zinc-950 border border-zinc-800 focus:border-blue-500 rounded-lg pl-10 pr-4 py-2.5 text-white placeholder:text-zinc-600 outline-none transition-colors"
                    placeholder="Nombre de tu empresa"
                  />
                </div>
              </div>

              {/* Submit */}
              <button
                type="submit"
                disabled={loading}
                className="w-full flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-500 disabled:bg-blue-600/50 text-white font-semibold py-3 rounded-lg transition-colors"
              >
                {loading ? (
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <>
                    Comenzar Prueba Gratis
                    <Zap className="w-4 h-4" />
                  </>
                )}
              </button>
            </form>

            <p className="text-center text-xs text-zinc-600 mt-4">
              Al registrarte, aceptas nuestros términos de servicio
            </p>

            <div className="mt-4 pt-4 border-t border-zinc-800 text-center">
              <Link
                to="/login"
                className="text-sm text-zinc-400 hover:text-white transition-colors"
              >
                ¿Ya tienes cuenta? <span className="text-blue-400">Inicia sesión</span>
              </Link>
            </div>
          </div>
          
          <div className="mt-4 text-center">
            <Link
              to="/landing"
              className="inline-flex items-center gap-2 text-zinc-500 hover:text-zinc-400 text-sm"
            >
              <ArrowLeft className="w-4 h-4" />
              Volver al inicio
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
